import React, { useState } from 'react';
import {
    Button,
    DropdownToggle,
    DropdownItem,
    DropdownMenu,
    Modal,
    ModalBody,
    ModalFooter,
    Dropdown,
} from 'reactstrap';

import ApiSubdomain from '../../../api/api-subdomain';
import Analytics from '../../../lib/user-analytics';
import Spinner from 'reactstrap/lib/Spinner';
import styled from 'styled-components';
import profileIconUrl from '../profile-icon-url';
import UriHelper from '../../../lib/uri-helper';
import SoarHelper from '../../../lib/soar-helper';

interface ProfileImageryEditPublicSelectorProps {
    listingId: number;
    isPublic: boolean;
    onChangeVisibility: (visible: boolean) => void;
}

const ImageryPublicSelector = ({ listingId, isPublic, onChangeVisibility }: ProfileImageryEditPublicSelectorProps) => {
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [showSoarPlusModal, setShowSoarPlusModal] = useState<boolean>(false);

    const toggleVisibility = (isVisible: boolean) => {
        if (SoarHelper.isSoar()) {
            setShowSoarPlusModal(true);
            Analytics.Event('My Dashboard', `Make Listing ${isVisible ? 'Public' : 'Private'} In Soar`, listingId);
            return;
        }

        if (isVisible === isPublic) return;

        setIsUpdating(true);
        ApiSubdomain.setListingVisibility(listingId, isVisible)
            .then(() => {
                onChangeVisibility(isVisible);
                Analytics.Event(
                    'My Dashboard',
                    `Make Listing ${isVisible ? 'Public' : 'Private'} In Subdomain`,
                    listingId
                );
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => setIsUpdating(false));
    };

    return (
        <React.Fragment>
            <DropdownSelector isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                <DropdownToggleSelector>
                    {isUpdating ? (
                        <Spinner color="warning" />
                    ) : (
                        <>
                            <DropdownToggleImg
                                src={
                                    isPublic
                                        ? profileIconUrl.editHideIconActiveUrl
                                        : profileIconUrl.editHideIconInActiveUrl
                                }
                            />
                            Visibility: {isPublic ? 'Public' : 'Private'}
                        </>
                    )}
                </DropdownToggleSelector>
                <DropdownMenu>
                    <DropdownItem onClick={() => toggleVisibility(true)}>Public (everyone)</DropdownItem>
                    <DropdownItem onClick={() => toggleVisibility(false)}>Private (only your users)</DropdownItem>
                </DropdownMenu>
            </DropdownSelector>

            <SoarPlusModal size="lg" centered isOpen={showSoarPlusModal} toggle={() => setShowSoarPlusModal(false)}>
                <ModalBody>
                    <SoarPlusPurchaseLogo>
                        <SoarPlusLogo src="/assets/subdomain/soar-plus-logo.png" />
                        <SoarPlusProPlan />
                    </SoarPlusPurchaseLogo>
                    <SoarPlusList>
                        <SoarPlusListItem>
                            <ListItemIcon src="/assets/subdomain/line-icon.png" />
                            Both public and private access to all content
                        </SoarPlusListItem>
                        <SoarPlusListItem>
                            <ListItemIcon src="/assets/subdomain/line-icon.png" />
                            Your own custom subdomain, <span>yourname.soar.earth</span>
                        </SoarPlusListItem>
                        <SoarPlusListItem>
                            <ListItemIcon src="/assets/subdomain/line-icon.png" />
                            Massive 100Gb of storage
                        </SoarPlusListItem>
                        <SoarPlusListItem>
                            <ListItemIcon src="/assets/subdomain/line-icon.png" />
                            Instant uploads, no approval period necessary
                        </SoarPlusListItem>
                    </SoarPlusList>
                    <SoarPlusFooter>
                        <UpsellModalButton
                            onClick={() => {
                                UriHelper.navigateToPath('/plus');
                                Analytics.Event('My Profile', 'Soar Plus Modal', 'Accepted Invite');
                            }}
                        >
                            UPGRADE NOW!
                        </UpsellModalButton>
                    </SoarPlusFooter>
                </ModalBody>
            </SoarPlusModal>
        </React.Fragment>
    );
};

export default ImageryPublicSelector;

const DropdownToggleImg = styled.img`
    width: 23px;
    height: 23px;
    padding-right: 2px;

    :focus {
        outline: none !important;
    }
`;

const DropdownSelector = styled(Dropdown)`
    background-color: transparent;

    button.btn.btn-secondary {
        margin: 0px;
        background-color: transparent !important;
        background-image: none;
        border: none !important;
        padding: 0px 0px !important;
        font-size: 14px;

        :hover {
            background-image: none;
            background-size: 0px 0px;
            border: none !important;
        }

        :focus {
            border: none !important;
        }
    }

    .dropdown-menu {
        background-color: transparent;
        position: absolute;
        left: 0px;
    }

    // TODO still needs a little more styling
    .dropdown-item {
        opacity: 1;
        color: white;
        margin-left: -20px;
        background-color: black;
        padding: 9px;
        border: 1px solid #eed926;
        width: 198px;
    }
`;

const DropdownToggleSelector = styled(DropdownToggle)`
    outline: none !important;

    :focus {
        outline: none !important;
    }
`;

const SoarPlusModal = styled(Modal)`
    max-width: 600px;
    .modal-content {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
            url('/assets/subdomain/subdomain-image.jpg');
        background-size: cover;
        border: 1px solid rgba(255, 255, 255, 0.3) !important ;
        border-radius: 6px;
    }
`;

const SoarPlusPurchaseLogo = styled.div`
    display: flex;
    flex-direction: row;
`;

const SoarPlusLogo = styled.img`
    width: 425px;

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`;

const SoarPlusProPlan = styled.div`
    background-image: url("data:image/svg+xml,%3Csvg width='106' height='64' viewBox='0 0 106 64' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.4833 0.875975C5.58276 0.875975 3.40912 0.915284 0.128906 1.22974V28.3514H6.13605V18.2889C7.71688 18.4854 9.49531 18.6034 11.2737 18.6034C18.0318 18.6034 20.4425 16.7166 20.4425 9.72001C20.4425 2.80201 18.0318 0.875975 10.4833 0.875975ZM10.3648 14.0045H6.13605V5.47488H10.3648C13.4474 5.47488 14.3959 6.33963 14.3959 9.72001C14.3959 13.1004 13.4474 13.9651 10.3648 14.0045Z' fill='white'/%3E%3Cpath d='M47.4849 19.9005C46.8525 17.1097 44.9951 16.127 43.6118 15.8126V15.616C47.4849 15.1444 48.8286 12.7073 48.8286 8.89457C48.8286 2.68409 45.8645 0.875975 38.7508 0.875975C33.8502 0.875975 31.6371 0.915284 28.3569 1.22974V28.3514H34.285V17.7779H38.5137C40.134 17.7779 40.9244 18.3282 41.4382 20.0577L43.4933 28.3514H49.8561L47.4849 19.9005ZM38.6322 13.1397H34.285V5.51418C35.5891 5.47488 37.0119 5.47488 38.6322 5.47488C41.7939 5.47488 42.7424 6.26101 42.7424 9.28764C42.7424 12.275 41.7939 13.1397 38.6322 13.1397Z' fill='white'/%3E%3Cpath d='M68.3765 0.71875C59.761 0.71875 56.7179 3.9026 56.7179 14.7906C56.7179 25.6786 59.761 28.8624 68.3765 28.8624C76.992 28.8624 80.0746 25.6786 80.0746 14.7906C80.0746 3.9026 76.992 0.71875 68.3765 0.71875ZM68.3765 5.55349C72.4866 5.55349 73.7118 7.59745 73.7118 14.7906C73.7118 21.9837 72.4866 24.0277 68.3765 24.0277C64.2663 24.0277 63.0412 21.9837 63.0412 14.7906C63.0412 7.59745 64.2663 5.55349 68.3765 5.55349Z' fill='white'/%3E%3Cpath d='M10.4833 36.3801C5.58276 36.3801 3.40912 36.4194 0.128906 36.7338V63.8555H6.13605V53.793C7.71688 53.9895 9.49531 54.1074 11.2737 54.1074C18.0318 54.1074 20.4425 52.2207 20.4425 45.2241C20.4425 38.3061 18.0318 36.3801 10.4833 36.3801ZM10.3648 49.5085H6.13605V40.979H10.3648C13.4474 40.979 14.3959 41.8437 14.3959 45.2241C14.3959 48.6045 13.4474 49.4692 10.3648 49.5085Z' fill='white'/%3E%3Cpath d='M34.285 36.7338H28.2778V58.1954C28.2778 61.5364 30.1353 63.9735 34.5221 63.9735C38.3951 63.9735 42.4262 63.9342 46.0621 63.5411L45.7855 58.9815H36.4191C34.8778 58.9815 34.285 58.3919 34.285 56.9768V36.7338Z' fill='white'/%3E%3Cpath d='M70.0996 63.8555H76.2254L68.3607 38.3061C68.1236 37.3234 67.2937 36.7338 66.2661 36.7338H60.5752C59.5871 36.7338 58.7177 37.3234 58.4806 38.3061L50.616 63.8555H56.7812L58.7572 56.741H68.1236L70.0996 63.8555ZM60.0219 52.1028L62.3141 43.8091L62.9069 41.2541H63.9739C64.0925 42.0796 64.3296 42.9836 64.5272 43.8091L66.8194 52.1028H60.0219Z' fill='white'/%3E%3Cpath d='M106 36.7338H100.309V52.3386C100.309 53.8716 100.427 55.3653 100.546 56.8982H100.151C99.5185 55.3653 98.7676 53.7144 98.1748 52.5352L91.2586 38.5026C90.6658 37.3234 90.1125 36.7338 88.8479 36.7338H85.1725C83.7497 36.7338 82.9988 37.4807 82.9988 38.8957V63.8555H88.7293V47.7397C88.7293 46.3247 88.6503 44.831 88.4527 43.416H88.8874C89.2036 44.7524 89.994 46.4426 90.5473 47.6218L97.7005 62.0867C98.2933 63.2659 98.8466 63.8555 100.111 63.8555H103.866C105.288 63.8555 106 63.1087 106 61.6937V36.7338Z' fill='white'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    width: 106px;
    height: 64px;
    margin-top: 27px;
`;

const SoarPlusFooter = styled(ModalFooter)`
    width: 100%;
    border-top: none;
`;

const UpsellModalButton = styled(Button)`
    background-color: #eed926 !important;
    color: black !important;
    font-size: 16px !important;
    margin-top: 0px;
    padding: 5px 10px !important;
    width: 100% !important;
`;

const SoarPlusList = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: 20px;
    padding-left: 20px;

    @media only screen and (max-width: 600px) {
        padding-top: 10px;
        padding-left: 0px;
    }
`;

const SoarPlusListItem = styled.div`
    color: white;
    padding-bottom: 20px;
    font-size: 18px;

    span {
        color: #eed926;
    }

    @media only screen and (max-width: 600px) {
        font-size: 13px;
        padding-bottom: 10px;
    }
`;

const ListItemIcon = styled.img`
    padding-right: 10px;

    @media only screen and (max-width: 600px) {
        padding-right: 5px;
        width: 18px;
        height: 14px;
        margin-top: -3px;
    }
`;
